// src/components/VimeoPlayerEmbed.jsx
//
// Wraps a Vimeo iframe with the @vimeo/player SDK so we can detect when the
// video can't be played in this iframe (most commonly because the video
// owner has restricted embedding to specific domains -> PrivacyError) and
// show a friendlier fallback card with a one-click "Open on Vimeo" button
// instead of the bare "Sorry, because of its privacy settings..." message
// Vimeo paints inside the iframe.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import './VimeoPlayerEmbed.css';

// Lazy-load the SDK so users who never open a Vimeo video don't pay the
// ~30 KB cost.
let cachedPlayerCtor = null;
async function loadPlayer() {
  if (cachedPlayerCtor) return cachedPlayerCtor;
  const mod = await import('@vimeo/player');
  cachedPlayerCtor = mod.default;
  return cachedPlayerCtor;
}

function describeError(name) {
  switch (name) {
    case 'PrivacyError':
      return 'The video owner has restricted embedding to specific domains, so it can’t play here.';
    case 'NotFoundError':
      return 'This video is no longer available on Vimeo.';
    case 'PasswordError':
      return 'This video is password-protected.';
    default:
      return 'Vimeo couldn’t play this video here.';
  }
}

export default function VimeoPlayerEmbed({ src, title, externalUrl }) {
  const iframeRef = useRef(null);
  const [errorName, setErrorName] = useState(null);
  // Track which src the current errorName belongs to, so we don't briefly
  // flash the fallback when the user clicks a new timestamp on the same video.
  const lastSrcRef = useRef(src);
  if (src !== lastSrcRef.current) {
    lastSrcRef.current = src;
    if (errorName !== null) setErrorName(null);
  }

  const reportError = useCallback((err) => {
    const name = (err && (err.name || err.message)) || 'PlayerError';
    setErrorName(String(name));
  }, []);

  useEffect(() => {
    if (errorName) return; // already showing fallback; skip player setup
    const iframe = iframeRef.current;
    if (!iframe) return;

    let cancelled = false;
    let player = null;

    loadPlayer()
      .then((Player) => {
        if (cancelled || !iframeRef.current) return;
        try {
          player = new Player(iframeRef.current);
        } catch (e) {
          // SDK couldn't attach for some reason - fall back silently to the
          // raw iframe; if the video really can't play, the user still has
          // the "Open ↗" link in the popup header.
          return;
        }
        player.on('error', (err) => {
          if (!cancelled) reportError(err);
        });
        // ready() rejects if the player can't initialize at all (e.g. the
        // iframe loaded into a blocked state and the SDK never gets to
        // a ready handshake).
        player.ready().catch((err) => {
          if (!cancelled) reportError(err);
        });
      })
      .catch(() => {
        // Dynamic import failed (offline, etc.) - leave the iframe alone.
      });

    return () => {
      cancelled = true;
      if (player) {
        try { player.off('error'); } catch (e) {}
        try { player.destroy(); } catch (e) {}
      }
    };
  }, [src, errorName, reportError]);

  if (errorName) {
    return (
      <div className="vimeo-blocked-card" role="alert">
        <div className="vimeo-blocked-icon" aria-hidden="true">
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"></line>
          </svg>
        </div>
        <h4 className="vimeo-blocked-title">This video can’t play here</h4>
        <p className="vimeo-blocked-detail">{describeError(errorName)}</p>
        {externalUrl && (
          <a
            className="vimeo-blocked-cta"
            href={externalUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open on Vimeo &rarr;
          </a>
        )}
      </div>
    );
  }

  return (
    <iframe
      ref={iframeRef}
      key={src}
      src={src}
      title={title || 'Vimeo video'}
      frameBorder="0"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
      allowFullScreen
    />
  );
}
